import { FeedbackCollector, FeedbackAnalytics } from '@core/feedback';

interface AnalyticsDashboardProps {
  collector: FeedbackCollector;
}

export function AnalyticsDashboard({ collector }: AnalyticsDashboardProps) {
  const analytics: FeedbackAnalytics = collector.generateAnalytics();
  const events = collector.getAllEvents();

  if (events.length === 0) {
    return (
      <div className="bg-white shadow rounded-lg p-12 text-center">
        <svg
          className="mx-auto h-12 w-12 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
          />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">No analytics data</h3>
        <p className="mt-1 text-sm text-gray-500">
          Use the Parser Demo to generate feedback and see analytics here.
        </p>
      </div>
    );
  }

  const { metrics, confidenceDistribution, categoryBreakdown, suggestions } = analytics;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Parsing Analytics
        </h2>
        <p className="text-sm text-gray-600">
          Period: {analytics.periodStart.toLocaleDateString()} - {analytics.periodEnd.toLocaleDateString()} •
          {' '}{events.length} total events
        </p>
      </div>

      {/* Accuracy Metrics */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Overall Accuracy</h3>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-green-50 rounded-lg p-4">
            <div className="text-sm text-green-600 font-medium">Overall</div>
            <div className="text-3xl font-bold text-green-900">
              {(metrics.accuracy * 100).toFixed(1)}%
            </div>
            <div className="text-xs text-green-600 mt-1">
              {metrics.correctParses} / {metrics.totalParses} correct
            </div>
          </div>

          <div className="bg-blue-50 rounded-lg p-4">
            <div className="text-sm text-blue-600 font-medium">Quantity</div>
            <div className="text-3xl font-bold text-blue-900">
              {(metrics.quantityAccuracy * 100).toFixed(1)}%
            </div>
          </div>

          <div className="bg-purple-50 rounded-lg p-4">
            <div className="text-sm text-purple-600 font-medium">Unit</div>
            <div className="text-3xl font-bold text-purple-900">
              {(metrics.unitAccuracy * 100).toFixed(1)}%
            </div>
          </div>

          <div className="bg-orange-50 rounded-lg p-4">
            <div className="text-sm text-orange-600 font-medium">Ingredient</div>
            <div className="text-3xl font-bold text-orange-900">
              {(metrics.ingredientAccuracy * 100).toFixed(1)}%
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-600 font-medium">Corrected</div>
            <div className="text-2xl font-bold text-gray-900">
              {metrics.correctedParses}
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-600 font-medium">Low Confidence</div>
            <div className="text-2xl font-bold text-gray-900">
              {metrics.lowConfidenceParses}
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-600 font-medium">Unknown</div>
            <div className="text-2xl font-bold text-gray-900">
              {metrics.unknownIngredients}
            </div>
          </div>
        </div>
      </div>

      {/* Confidence Distribution */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Confidence Distribution</h3>

        <div className="space-y-3">
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-600">High (&gt; 90%)</span>
              <span className="font-semibold">{confidenceDistribution.high}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-green-500 h-2 rounded-full"
                style={{
                  width: `${(confidenceDistribution.high / events.length) * 100}%`,
                }}
              />
            </div>
          </div>

          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-600">Medium (70-90%)</span>
              <span className="font-semibold">{confidenceDistribution.medium}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-yellow-500 h-2 rounded-full"
                style={{
                  width: `${(confidenceDistribution.medium / events.length) * 100}%`,
                }}
              />
            </div>
          </div>

          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-600">Low (&lt; 70%)</span>
              <span className="font-semibold">{confidenceDistribution.low}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-red-500 h-2 rounded-full"
                style={{
                  width: `${(confidenceDistribution.low / events.length) * 100}%`,
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Category Breakdown */}
      {Object.keys(categoryBreakdown).length > 0 && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Category Breakdown</h3>

          <div className="space-y-3">
            {Object.entries(categoryBreakdown)
              .sort((a, b) => b[1].count - a[1].count)
              .map(([category, data]) => (
                <div key={category} className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium text-gray-900 uppercase">{category}</span>
                      <span className="text-gray-600">
                        {data.count} items • {(data.accuracy * 100).toFixed(0)}% accurate
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          data.accuracy > 0.9
                            ? 'bg-green-500'
                            : data.accuracy > 0.7
                            ? 'bg-yellow-500'
                            : 'bg-red-500'
                        }`}
                        style={{ width: `${data.accuracy * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Top Failures */}
      {metrics.topFailures.length > 0 && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Most Common Failures</h3>

          <div className="space-y-2">
            {metrics.topFailures.slice(0, 5).map((failure, i) => (
              <div
                key={i}
                className="flex items-center justify-between p-3 bg-red-50 rounded-lg"
              >
                <div className="flex-1">
                  <div className="font-mono text-sm text-gray-900">{failure.input}</div>
                  <div className="text-xs text-gray-600">
                    Confidence: {(failure.averageConfidence * 100).toFixed(0)}%
                  </div>
                </div>
                <span className="ml-4 px-3 py-1 bg-red-100 text-red-800 text-sm font-semibold rounded-full">
                  {failure.count}×
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top Corrections */}
      {metrics.topCorrections.length > 0 && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Most Common Corrections</h3>

          <div className="space-y-2">
            {metrics.topCorrections.slice(0, 5).map((correction, i) => (
              <div
                key={i}
                className="flex items-center justify-between p-3 bg-blue-50 rounded-lg"
              >
                <div className="flex-1">
                  <div className="text-sm">
                    <span className="font-mono text-gray-600">{correction.original}</span>
                    <span className="mx-2 text-gray-400">→</span>
                    <span className="font-mono font-semibold text-gray-900">
                      {correction.corrected}
                    </span>
                  </div>
                </div>
                <span className="ml-4 px-3 py-1 bg-blue-100 text-blue-800 text-sm font-semibold rounded-full">
                  {correction.count}×
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Suggestions for Improvement */}
      {suggestions.length > 0 && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Ingredient Suggestions
            <span className="ml-2 text-sm font-normal text-gray-500">
              (≥3 corrections)
            </span>
          </h3>

          <div className="space-y-3">
            {suggestions.map((suggestion, i) => (
              <div
                key={i}
                className={`p-4 rounded-lg ${
                  suggestion.needsReview ? 'bg-yellow-50' : 'bg-green-50'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="text-sm">
                      <span className="font-mono text-gray-600">{suggestion.original}</span>
                      <span className="mx-2 text-gray-400">→</span>
                      <span className="font-mono font-semibold text-gray-900">
                        {suggestion.canonical}
                      </span>
                    </div>
                    <div className="mt-1 text-xs text-gray-600">
                      {suggestion.frequency} corrections • {(suggestion.confidence * 100).toFixed(0)}% confidence
                      {suggestion.needsReview && (
                        <span className="ml-2 text-yellow-600">• Needs review</span>
                      )}
                    </div>
                    <div className="mt-2 text-xs text-gray-500">
                      Examples: {suggestion.examples.slice(0, 2).join(', ')}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
